import React, { ReactNode, useEffect, useState } from "react";
import {
  AppBar,
  Box,
  Button,
  Container,
  CssBaseline,
  Drawer,
  IconButton,
  Link,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  MenuItem,
  Paper,
  styled,
  Toolbar,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import { Outlet, useNavigate } from "react-router";
import DttFooter from "@/components/DttFooter";
import EducatorRegisterHeader from "@/components/EducatorRegisterHeader";
import { LogOutIcon, Menu, MenuIcon } from "lucide-react";
import { useUser } from "@/contexts/UserContext";
import TCCDeadlineBanner from "@/components/TCCDeadlineBanner";

const NavLink = styled(Link)(({ theme }) => ({
  color: "#0066FF",
  textDecoration: "none",
  marginLeft: theme.spacing(3),
  "&:hover": {
    textDecoration: "underline",
  },
  fontFamily: "Roboto",
}));

const RulesAndGuidelinesLayout = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const [mobileOpen, setMobileOpen] = React.useState(false);
  const navigate = useNavigate();
  const { user } = useUser();
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);

  const handleUserMenuClick = (event: React.MouseEvent<HTMLElement>) => {
    document.body.classList.remove("ro-popover-open");
    setAnchorEl(event.currentTarget);
  };
  const handleUserMenuClose = () => {
    setAnchorEl(null);
  };

  const [sidebarOpen, setSidebarOpen] = useState(!isMobile);

  const toggleSidebar = () => {
    setSidebarOpen(!sidebarOpen);
  };

  const handleDrawerToggle = () => {
    setMobileOpen(!mobileOpen);
  };

  useEffect(() => {
    setSidebarOpen(!isMobile);
  }, [isMobile]);

  const navItems = [
    { name: "Overview", path: "../#overview" },
    { name: "How It Works", path: `../#how-it-works` },
    { name: "Timeline", path: `../#timeline` },
    { name: "Judges", path: `../#judges` },
    { name: "Rules", path: `../#rules` },
    { name: "FAQ", path: `../#faq` },
  ];
  const drawer = (
    <Box onClick={handleDrawerToggle} sx={{ textAlign: "center", p: 2 }}>
      <List>
        {navItems.map((item) => (
          // <ListItem key={item.name} component={Link} href={item.href} sx={{ color: "#000", textDecoration: "none" }}>
          //   <ListItemText primary={item.name} />
          // </ListItem>
          <ListItem key={item.name}>
            <ListItemButton onClick={() => navigate(item.path)}>
              <ListItemText primary={item.name} />
            </ListItemButton>
          </ListItem>
        ))}
      </List>
    </Box>
  );
  return (
    <>
      {/* <CustomBreadcrumbProvider> */}
      {/* <Container maxWidth="xl"> */}
      <Box
        sx={{
          // backgroundColor: "#f0f7ff",
          backgroundColor: "#f8fafc",
          display: "flex",
          flexDirection: "column",
        }}
      >
        <CssBaseline />

        <AppBar position="static" sx={{ backgroundColor: "#fff" }}>
          <Toolbar sx={{ maxWidth: "lg", width: "100%", margin: "0 auto" }}>
            <Box sx={{ flexGrow: 1 }}>
              <img
                src="https://pythaverse.space/wp-content/uploads/2023/02/18b214a6c0c81d9644d9.png"
                alt="Pythaverse Logo"
                width="200"
                height="50"
                style={{ objectFit: "contain" }}
              />
            </Box>
            {isMobile ? (
              <>
                <IconButton
                  color="inherit"
                  aria-label="open drawer"
                  edge="end"
                  onClick={handleDrawerToggle}
                >
                  <MenuIcon />
                </IconButton>
                <Drawer
                  anchor="right"
                  open={mobileOpen}
                  onClose={handleDrawerToggle}
                  ModalProps={{
                    keepMounted: true,
                  }}
                >
                  {drawer}
                </Drawer>
              </>
            ) : (
              <Box sx={{ display: "flex", alignItems: "center" }}>
                {navItems.map((item) => (
                  <NavLink
                    key={item.name}
                    onClick={() => navigate(item.path)}
                    sx={{ cursor: "pointer" }}
                  >
                    {item.name}
                  </NavLink>
                ))}
                {user?.user_login ? (
                  <Button
                    size="small"
                    variant="contained"
                    onClick={handleUserMenuClick}
                    color="primary"
                    title={user?.display_name}
                    sx={{
                      textTransform: "none",
                      borderRadius: "30px",
                      "& .MuiTypography-root": {
                        color: "white",
                      },
                      fontFamily: "Roboto",
                      fontStyle: "normal",
                      fontWeight: 700,
                      fontSize: "16px",
                      lineHeight: "19px",
                      textDecoration: "none",
                      color: "#F5F5F5",
                      marginLeft: "50px",
                    }}
                  >
                    <Typography variant="h6" noWrap component="div" ml={1}>
                      {user?.display_name}
                    </Typography>
                  </Button>
                ) : (
                  <NavLink key="btnLogin" href={user?.login_url}>
                    Login
                  </NavLink>
                )}
              </Box>
            )}
            <Menu
              anchorEl={anchorEl}
              anchorOrigin={{
                vertical: "bottom",
                horizontal: "left",
              }}
              transformOrigin={{
                vertical: "top",
                horizontal: "left",
              }}
              open={Boolean(anchorEl)}
              onClose={handleUserMenuClose}
            >
              <MenuItem
                onClick={() => (window.location.href = user?.logout_url || "/")}
              >
                <LogOutIcon size={20} />
                <Typography variant="inherit" noWrap component="span" ml={1}>
                  Logout
                </Typography>
              </MenuItem>
            </Menu>
          </Toolbar>
        </AppBar>
        <TCCDeadlineBanner />
        <Container>
          <Outlet />
        </Container>
        <Box component="footer" sx={{ bgcolor: "#f5f5f5", py: 3, mt: 4 }}>
          <Typography variant="body2" color="text.secondary" align="center">
            © 2025 Pythaverse Educators ThoughtCoCreate. All rights reserved.
          </Typography>
        </Box>
      </Box>
    </>
  );
};

export default RulesAndGuidelinesLayout;
